"use client";

import React, { createContext, useContext, useEffect, ReactNode, useMemo } from "react";
import { hexToTailwindHSL } from "../utils/colorUtils";
import { resolveAssetUrl } from "../utils/assetUrls";
import type { Theme, StylingContextType } from "../types/theme-types";

interface StylingContextTypeInternal extends StylingContextType {
  /** Computed CSS custom properties — apply these directly onto the themed element's style prop */
  cssVariables: Record<string, string>;
}

const StylingContext = createContext<StylingContextTypeInternal | undefined>(undefined);

interface StylingProviderProps {
  children: ReactNode;
  theme?: Theme | null;
  chameleonMode?: boolean;
  embeddedMode?: boolean;
}

export const StylingProvider: React.FC<StylingProviderProps> = ({
  children,
  theme = null,
  chameleonMode = false,
  embeddedMode = false,
}) => {
  // Memoize CSS variable map from the active theme
  const cssVariables = useMemo(() => {
    if (!theme) return {};

    const vars: Record<string, string> = {};
    const { colors, borderRadius, layout } = theme.config;

    if (colors.primary) vars["--primary"] = hexToTailwindHSL(colors.primary);
    if (colors.secondary) vars["--secondary"] = hexToTailwindHSL(colors.secondary);
    if (colors.accent) vars["--accent"] = hexToTailwindHSL(colors.accent);
    if (colors.background) vars["--background"] = hexToTailwindHSL(colors.background);
    if (colors.foreground) {
      vars["--foreground"] = hexToTailwindHSL(colors.foreground);
      vars["--card-foreground"] = hexToTailwindHSL(colors.foreground);
    }
    if (colors.formBackground) vars["--card"] = hexToTailwindHSL(colors.formBackground);

    if (borderRadius?.default) vars["--radius"] = borderRadius.default;

    if (theme.config.typography) {
      const { typography } = theme.config;
      if (typography.fontFamily) vars["--font-family"] = `"${typography.fontFamily}", sans-serif`;
      if (typography.headings) vars["--font-heading"] = `"${typography.headings}", sans-serif`;
      if (typography.body) vars["--font-body"] = `"${typography.body}", sans-serif`;
      if (typography.labels) vars["--font-label"] = `"${typography.labels}", sans-serif`;
      if (typography.buttons) vars["--font-button"] = `"${typography.buttons}", sans-serif`;
    }

    if (layout?.formWidth) {
      vars["--form-width"] = `${layout.formWidth}px`;
    }

    if (embeddedMode && layout?.embedMargin !== undefined) {
      vars["--embed-margin"] = `${layout.embedMargin}px`;
    }

    const backgroundUrl = resolveAssetUrl(theme.assets?.background);
    if (!embeddedMode && backgroundUrl) {
      vars["--bg-image"] = `url("${backgroundUrl}")`;
      const bgMode = layout?.backgroundMode || "cover";
      if (bgMode === "cover") {
        vars["--bg-repeat"] = "no-repeat";
        vars["--bg-size"] = "cover";
        vars["--bg-position"] = "center";
        vars["--bg-attachment"] = "fixed";
      } else if (bgMode === "stretch") {
        vars["--bg-repeat"] = "no-repeat";
        vars["--bg-size"] = "100% 100%";
        vars["--bg-position"] = "center";
        vars["--bg-attachment"] = "fixed";
      } else if (bgMode === "tile") {
        vars["--bg-repeat"] = "repeat";
        vars["--bg-size"] = "auto";
        vars["--bg-position"] = "top left";
        vars["--bg-attachment"] = "scroll";
      }
    }

    return vars;
  }, [theme, embeddedMode]);

  // Dynamically load Google Fonts for any typography settings
  useEffect(() => {
    if (!theme?.config?.typography) return;

    const fontsToLoad = new Set<string>();
    const { typography } = theme.config;

    [
      typography.fontFamily,
      typography.headings,
      typography.body,
      typography.labels,
      typography.buttons,
    ].forEach((font) => {
      if (font && font !== "inherit") fontsToLoad.add(font);
    });

    if (fontsToLoad.size === 0) return;

    const fontFamilies = Array.from(fontsToLoad).map((f) => f.replace(/ /g, "+"));
    const familyQuery = fontFamilies.map((f) => `family=${f}:wght@400;500;600;700`).join("&");
    const href = `https://fonts.googleapis.com/css2?${familyQuery}&display=swap`;

    let link = document.getElementById("dynamic-google-fonts") as HTMLLinkElement;
    if (!link) {
      link = document.createElement("link");
      link.id = "dynamic-google-fonts";
      link.rel = "stylesheet";
      document.head.appendChild(link);
    }

    if (link.href !== href) {
      link.href = href;
    }
  }, [theme?.config?.typography]);

  const value = useMemo(
    () => ({
      theme,
      isChameleonMode: chameleonMode,
      cssVariables,
    }),
    [theme, chameleonMode, cssVariables]
  );

  // IMPORTANT: We do NOT wrap children in a `display:contents` div here.
  // A display:contents element still causes CSS custom properties to cascade
  // via inheritance into ALL descendant elements, including builder-internal
  // Tailwind utility classes like bg-background, bg-primary, text-foreground.
  // This would incorrectly style the form builder canvas with the custom theme.
  //
  // Instead, callers retrieve `cssVariables` from useStyling() and apply them
  // directly on the specific element that should be themed (data-theme-wrapper).
  return <StylingContext.Provider value={value}>{children}</StylingContext.Provider>;
};

export const useStyling = () => {
  const context = useContext(StylingContext);
  if (context === undefined) {
    throw new Error("useStyling must be used within a StylingProvider");
  }
  return context;
};
